import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MaintenanceType,
    MaintenanceReportFormData,
    ChecklistItem,
    MaintenanceReport,
    ControlCenterType,
    ControlCenterEntry,
    HoodEntry,
    HoodTechnology,
    BeforeAfterImage,
    MobichefPressure
} from '../types';
import { useSettings } from '../contexts/SettingsContext';
import {
    generateMaintenanceReportNumber,
    saveMaintenanceReport,
    generateId
} from '../utils';

interface MaintenanceReportFormProps {
    onSave: () => void;
    onPreview: (report: MaintenanceReport) => void;
    editReport?: MaintenanceReport | null;
}

// Control center type options
const CONTROL_CENTER_TYPES: ControlCenterType[] = [
    'none',
    'hmi_lhc2',
    'hts_lhc1',
    'got_mini_111',
    'got_mini_112',
    'bluetooth_console',
    'legacy_controller',
    'custom'
];

// Hood technology options
const HOOD_TECHNOLOGIES: HoodTechnology[] = ['uv', 'ww', 'ansul', 'marvel', 'cold_mist', 'pollustop'];

// Maintenance type to icon mapping
const TYPE_ICONS: Record<MaintenanceType, string> = {
    kcw_ww_technical: 'engineering',
    kcw_annual_cleaning: 'cleaning_services',
    uv_service: 'light_mode',
    ansul_r102: 'local_fire_department',
    marvel_dcv: 'air',
    cold_mist: 'ac_unit',
    mobichef: 'restaurant'
};

// Initial form data
const getInitialFormData = (): MaintenanceReportFormData => ({
    customerName: '',
    customerAddress: '',
    siteName: '',
    siteAddress: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    date: new Date().toISOString().split('T')[0],
    technician: '',
    types: ['kcw_ww_technical'],
    controlCenters: [{ id: generateId(), type: 'hmi_lhc2' }],
    hoods: [],
    generalNotes: ''
});

const MaintenanceReportForm: React.FC<MaintenanceReportFormProps> = ({ onSave, onPreview, editReport }) => {
    const { t } = useTranslation();
    const { settings } = useSettings();

    const [formData, setFormData] = useState<MaintenanceReportFormData>(getInitialFormData());
    const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
    const [generalImages, setGeneralImages] = useState<string[]>([]);
    const [beforeAfterImages, setBeforeAfterImages] = useState<BeforeAfterImage[]>([]);
    const [activeSection, setActiveSection] = useState<'type' | 'general' | 'equipment' | 'checklist' | 'images'>('type');

    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof MaintenanceReportFormData, string>>>({});

    // Load edit report if provided
    useEffect(() => {
        if (editReport) {
            setFormData(editReport.formData);
            setChecklist(editReport.checklist);
            setGeneralImages(editReport.generalImages || []);
            setBeforeAfterImages(editReport.beforeAfterImages || []);
        }
    }, [editReport]);

    // Generate checklist based on selected types and technologies (with deduplication)
    const generateChecklist = useMemo(() => {
        const addedKeys = new Set<string>(); // Track added labelKeys to prevent duplicates
        const items: ChecklistItem[] = [];
        const types = formData.types;

        // Helper to add item only if not already added
        const addItem = (labelKey: string) => {
            if (!addedKeys.has(labelKey)) {
                addedKeys.add(labelKey);
                items.push({
                    id: generateId(),
                    labelKey,
                    label: t(labelKey),
                    status: '',
                    notes: ''
                });
            }
        };

        // Common items for all types (always added first)
        const commonKeys = [
            'maintenance.checklist.common.controlCenterStatus',
            'maintenance.checklist.common.softwareVersion',
            'maintenance.checklist.common.communicationCheck',
            'maintenance.checklist.common.cableConnections',
            'maintenance.checklist.common.safetyDevices'
        ];
        commonKeys.forEach(addItem);

        // Collect all technologies from hoods
        const allTechs = new Set<HoodTechnology>();
        formData.hoods.forEach(hood => hood.technologies.forEach(tech => allTechs.add(tech)));

        // Process each selected type
        types.forEach(type => {
            if (type === 'kcw_ww_technical') {
                const kcwKeys = ['airflowMeasurement', 'filterCondition', 'lightingCheck', 'sealCondition', 'greaseTrapCheck'];
                kcwKeys.forEach(key => addItem(`maintenance.checklist.kcw_technical.${key}`));

                // Add technology-specific items based on hoods
                if (allTechs.has('uv')) {
                    const uvKeys = ['uvTubeCondition', 'uvHoursReading', 'uvTubeReplacement', 'uvBallastCheck', 'uvSafetyInterlock', 'uvCleaningRequired'];
                    uvKeys.forEach(key => addItem(`maintenance.checklist.uv.${key}`));
                }
                if (allTechs.has('ww')) {
                    const wwKeys = ['washSystemTest', 'nozzleCheck', 'pumpPressure', 'detergentLevel', 'drainCheck', 'filterCondition'];
                    wwKeys.forEach(key => addItem(`maintenance.checklist.ww.${key}`));
                }
                if (allTechs.has('ansul')) {
                    const ansulKeys = ['systemArmed', 'detectorCheck', 'nozzleCoverage', 'tankPressure', 'manualRelease'];
                    ansulKeys.forEach(key => addItem(`maintenance.checklist.ansul.${key}`));
                }
                if (allTechs.has('marvel')) {
                    const marvelKeys = ['sensorCalibration', 'airflowMeasurement', 'responseTest', 'setpointVerification', 'damperOperation'];
                    marvelKeys.forEach(key => addItem(`maintenance.checklist.marvel.${key}`));
                }
                if (allTechs.has('cold_mist')) {
                    const coldMistKeys = ['mistGeneratorCheck', 'waterSupply', 'filterMaintenance', 'drainageCheck'];
                    coldMistKeys.forEach(key => addItem(`maintenance.checklist.cold_mist.${key}`));
                }
            }

            if (type === 'kcw_annual_cleaning') {
                const cleaningKeys = ['preInspection', 'deepCleaning', 'filterWashing', 'ductInspection', 'surfaceCleaning', 'postInspection', 'beforeAfterPhotos'];
                cleaningKeys.forEach(key => addItem(`maintenance.checklist.kcw_cleaning.${key}`));
            }

            if (type === 'uv_service') {
                const uvKeys = ['uvTubeCondition', 'uvHoursReading', 'uvTubeReplacement', 'uvBallastCheck', 'uvSafetyInterlock', 'uvCleaningRequired'];
                uvKeys.forEach(key => addItem(`maintenance.checklist.uv.${key}`));
            }

            if (type === 'ansul_r102') {
                const ansulKeys = ['systemArmed', 'detectorCheck', 'nozzleCoverage', 'tankPressure', 'manualRelease', 'certification', 'ansulLabel'];
                ansulKeys.forEach(key => addItem(`maintenance.checklist.ansul.${key}`));
            }

            if (type === 'marvel_dcv') {
                const marvelKeys = ['sensorCalibration', 'airflowMeasurement', 'responseTest', 'setpointVerification', 'damperOperation'];
                marvelKeys.forEach(key => addItem(`maintenance.checklist.marvel.${key}`));
            }

            if (type === 'cold_mist') {
                const coldMistKeys = ['mistGeneratorCheck', 'waterSupply', 'filterMaintenance', 'drainageCheck'];
                coldMistKeys.forEach(key => addItem(`maintenance.checklist.cold_mist.${key}`));

                // Check if any hood has WW
                if (allTechs.has('ww')) {
                    const wwKeys = ['washSystemTest', 'nozzleCheck', 'pumpPressure', 'detergentLevel', 'drainCheck'];
                    wwKeys.forEach(key => addItem(`maintenance.checklist.ww.${key}`));
                }
            }

            if (type === 'mobichef') {
                const mobichefKeys = ['hmiStatusCheck', 'hmiAlarms', 'softwareUpdate', 'filterSeparation', 'serviceIntervals', 'filterReplacement', 'airflowCalibration', 'fanMaintenance', 'wearPartsCheck', 'documentationComplete'];
                mobichefKeys.forEach(key => addItem(`maintenance.checklist.mobichef.${key}`));
            }
        });

        // Add final common items (always at end)
        const endKeys = [
            'maintenance.checklist.common.documentation',
            'maintenance.checklist.common.cleaning',
            'maintenance.checklist.common.wasteDisposal'
        ];
        endKeys.forEach(addItem);

        return items;
    }, [formData.types, formData.hoods, t]);

    // Update checklist when type or hoods change
    useEffect(() => {
        if (!editReport) {
            setChecklist(generateChecklist);
        }
    }, [generateChecklist, editReport]);

    // Calculate progress
    const progress = useMemo(() => {
        if (checklist.length === 0) return 0;
        const completed = checklist.filter(item => item.status).length;
        return Math.round((completed / checklist.length) * 100);
    }, [checklist]);

    // Handlers
    const handleSectionChange = (section: typeof activeSection) => {
        setActiveSection(section);
        window.scrollTo({ top: 100, behavior: 'smooth' });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (fieldErrors[name as keyof MaintenanceReportFormData]) {
            setFieldErrors(prev => {
                const next = { ...prev };
                delete next[name as keyof MaintenanceReportFormData];
                return next;
            });
        }
    };

    const validateForArchiveSave = (): boolean => {
        const errors: Partial<Record<keyof MaintenanceReportFormData, string>> = {};
        if (!formData.customerName.trim()) errors.customerName = t('validation.required');
        if (!formData.date.trim()) errors.date = t('validation.required');
        if (!formData.technician.trim()) errors.technician = t('validation.required');

        setFieldErrors(errors);

        const isValid = Object.keys(errors).length === 0;
        if (!isValid) {
            // Ensure errors are visible
            setActiveSection('general');
            window.scrollTo({ top: 100, behavior: 'smooth' });
        }
        return isValid;
    };

    const handleTypeChange = (type: MaintenanceType) => {
        setFormData(prev => {
            const hasType = prev.types.includes(type);
            // Toggle: remove if already selected, add if not
            // Ensure at least one type is always selected
            if (hasType && prev.types.length > 1) {
                return { ...prev, types: prev.types.filter(t => t !== type) };
            } else if (!hasType) {
                return { ...prev, types: [...prev.types, type] };
            }
            return prev;
        });
    };

    // Control Center handlers
    const addControlCenter = () => {
        setFormData(prev => ({
            ...prev,
            controlCenters: [...prev.controlCenters, { id: generateId(), type: 'hmi_lhc2' }]
        }));
    };

    const removeControlCenter = (id: string) => {
        setFormData(prev => ({
            ...prev,
            controlCenters: prev.controlCenters.filter(cc => cc.id !== id)
        }));
    };

    const updateControlCenter = (id: string, field: keyof ControlCenterEntry, value: string) => {
        setFormData(prev => ({
            ...prev,
            controlCenters: prev.controlCenters.map(cc => {
                if (cc.id !== id) return cc;
                const updated = { ...cc, [field]: value };
                // Clear stale fields when switching type
                if (field === 'type') {
                    if (value === 'none') {
                        delete updated.serialNumber;
                        delete updated.customName;
                        delete updated.customDescription;
                    } else if (value === 'custom') {
                        delete updated.serialNumber;
                    } else {
                        delete updated.customName;
                        delete updated.customDescription;
                    }
                }
                return updated;
            })
        }));
    };

    // Hood handlers
    const addHood = () => {
        const newHood: HoodEntry = {
            id: generateId(),
            name: `${t('maintenance.hoods.title')} ${formData.hoods.length + 1}`,
            technologies: [],
            notes: ''
        };
        setFormData(prev => ({ ...prev, hoods: [...prev.hoods, newHood] }));
    };

    const removeHood = (id: string) => {
        setFormData(prev => ({
            ...prev,
            hoods: prev.hoods.filter(h => h.id !== id)
        }));
    };

    const updateHood = (id: string, field: keyof HoodEntry, value: any) => {
        setFormData(prev => ({
            ...prev,
            hoods: prev.hoods.map(h =>
                h.id === id ? { ...h, [field]: value } : h
            )
        }));
    };

    const toggleHoodTechnology = (hoodId: string, tech: HoodTechnology) => {
        setFormData(prev => ({
            ...prev,
            hoods: prev.hoods.map(h => {
                if (h.id !== hoodId) return h;
                const hasTech = h.technologies.includes(tech);
                return {
                    ...h,
                    technologies: hasTech
                        ? h.technologies.filter(t => t !== tech)
                        : [...h.technologies, tech]
                };
            })
        }));
    };

    // Checklist handlers
    const handleChecklistChange = (id: string, field: 'status' | 'notes', value: string) => {
        setChecklist(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    // Image handlers
    const handleGeneralImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const readers = files.map(file => {
                return new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(file);
                });
            });
            Promise.all(readers).then(newImages => {
                setGeneralImages(prev => [...prev, ...newImages]);
            });
        }
    };

    const removeGeneralImage = (index: number) => {
        setGeneralImages(prev => prev.filter((_, i) => i !== index));
    };

    // Before/After image handlers
    const addBeforeAfterPair = () => {
        setBeforeAfterImages(prev => [...prev, { id: generateId() }]);
    };

    const removeBeforeAfterPair = (id: string) => {
        setBeforeAfterImages(prev => prev.filter(pair => pair.id !== id));
    };

    const handleBeforeAfterUpload = (pairId: string, type: 'before' | 'after', e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setBeforeAfterImages(prev => prev.map(pair =>
                    pair.id === pairId ? { ...pair, [type]: reader.result as string } : pair
                ));
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    // Mobichef pressure handlers
    const handlePressureChange = (field: keyof MobichefPressure, value: string) => {
        setFormData(prev => ({
            ...prev,
            mobichefPressure: {
                ...prev.mobichefPressure,
                [field]: value ? Number(value) : undefined
            }
        }));
    };

    // Submit handler - saves and opens print preview
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const report: MaintenanceReport = {
            id: editReport?.id || generateId(),
            documentNumber: editReport?.documentNumber || generateMaintenanceReportNumber(),
            formData,
            checklist,
            generalImages,
            beforeAfterImages,
            settings: {
                printLanguage: settings.printLanguage,
                companyCountry: settings.companyCountry
            },
            createdAt: editReport?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        saveMaintenanceReport(report);
        onSave();
        onPreview(report);
    };

    // Save to archive without opening print preview
    const handleSaveToArchive = () => {
        const isValid = validateForArchiveSave();
        const savingWithWarnings = !isValid;
        if (!isValid) {
            const confirmMessage = t('validation.confirmProceedWithWarnings');
            if (!window.confirm(confirmMessage)) {
                return;
            }
        }

        const report: MaintenanceReport = {
            id: editReport?.id || generateId(),
            documentNumber: editReport?.documentNumber || generateMaintenanceReportNumber(),
            formData,
            checklist,
            generalImages,
            beforeAfterImages,
            settings: {
                printLanguage: settings.printLanguage,
                companyCountry: settings.companyCountry
            },
            createdAt: editReport?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        saveMaintenanceReport(report);
        onSave();

        // Clear the form only when saving a fully valid report.
        // If user proceeds with warnings, keep the form so missing fields can be filled.
        if (!savingWithWarnings) {
            setFormData(getInitialFormData());
            setChecklist([]);
            setGeneralImages([]);
            setBeforeAfterImages([]);
            setActiveSection('type');
            setFieldErrors({});
        }
        
        // Show confirmation
        alert(t('maintenance.savedToArchive') || 'Report saved to archive!');
    };

    const clearForm = () => {
        if (window.confirm(t('common.confirm') + '?')) {
            setFormData(getInitialFormData());
            setChecklist([]);
            setGeneralImages([]);
            setBeforeAfterImages([]);
            setActiveSection('type');
            setFieldErrors({});
        }
    };

    return (
        <form onSubmit={handleSubmit} className="animate-fade-in">
            <div className="maintenance-layout">
                {/* Sidebar */}
                <div className="maintenance-sidebar">
                    <div className="sidebar-progress">
                        <div className="sidebar-progress-label">{t('maintenance.progress')}</div>
                        <div className="flex items-center space-x-2">
                            <div className="progress-bar-track" style={{ flex: 1 }}>
                                <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                            </div>
                            <span className="sidebar-progress-value">{progress}%</span>
                        </div>
                    </div>

                    <button type="button" onClick={() => handleSectionChange('type')} className={`maintenance-nav-item ${activeSection === 'type' ? 'active' : ''}`}>
                        <span className="material-symbols-outlined text-sm mr-2">category</span>
                        <span>{t('maintenance.selectType')}</span>
                    </button>

                    <button type="button" onClick={() => handleSectionChange('general')} className={`maintenance-nav-item ${activeSection === 'general' ? 'active' : ''}`}>
                        <span className="material-symbols-outlined text-sm mr-2">info</span>
                        <span>{t('maintenance.general.title')}</span>
                    </button>

                    <button type="button" onClick={() => handleSectionChange('equipment')} className={`maintenance-nav-item ${activeSection === 'equipment' ? 'active' : ''}`}>
                        <span className="material-symbols-outlined text-sm mr-2">kitchen</span>
                        <span>{t('maintenance.hoods.title')}</span>
                        <span className="nav-item-count">{formData.hoods.length}</span>
                    </button>

                    <button type="button" onClick={() => handleSectionChange('checklist')} className={`maintenance-nav-item ${activeSection === 'checklist' ? 'active' : ''}`}>
                        <span className="material-symbols-outlined text-sm mr-2">checklist</span>
                        <span>{t('maintenance.checklist.title')}</span>
                        <span className="nav-item-count">{checklist.filter(i => i.status).length}/{checklist.length}</span>
                    </button>

                    <button type="button" onClick={() => handleSectionChange('images')} className={`maintenance-nav-item ${activeSection === 'images' ? 'active' : ''}`}>
                        <span className="material-symbols-outlined text-sm mr-2">photo_library</span>
                        <span>{t('maintenance.images.title')}</span>
                        <span className="nav-item-count">{generalImages.length + beforeAfterImages.length}</span>
                    </button>

                    {/* Desktop Action Buttons */}
                    <div className="sidebar-actions hidden lg:flex">
                        <button type="submit" className="btn btn-primary w-full justify-center">
                            <span className="material-symbols-outlined text-sm">print</span>
                            {t('maintenance.actions.print')}
                        </button>
                        <button type="button" onClick={handleSaveToArchive} className="btn btn-success w-full justify-center">
                            <span className="material-symbols-outlined text-sm">save</span>
                            {t('maintenance.actions.save')}
                        </button>
                        <button type="button" onClick={clearForm} className="btn btn-secondary w-full justify-center">
                            <span className="material-symbols-outlined text-sm">delete_sweep</span>
                            {t('maintenance.actions.clear')}
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="maintenance-content space-y-6">
                    {/* Mobile Progress Bar */}
                    <div className="lg:hidden mobile-progress-panel">
                        <div className="flex justify-between sidebar-progress-label" style={{ marginBottom: '8px' }}>
                            <span>{t('maintenance.progress')}</span>
                            <span>{progress}%</span>
                        </div>
                        <div className="progress-bar-track">
                            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>

                    {/* TYPE SELECTION SECTION */}
                    <div className={activeSection === 'type' ? 'block' : 'hidden'}>
                        <div className="section-panel">
                            <h2 className="section-panel-heading">{t('maintenance.selectType')}</h2>
                            <p className="section-description">{t('maintenance.selectTypeHint', 'Select one or more service types. Checklists will be combined.')}</p>
                            <div className="report-type-grid">
                                {(['kcw_ww_technical', 'kcw_annual_cleaning', 'uv_service', 'ansul_r102', 'marvel_dcv', 'cold_mist', 'mobichef'] as MaintenanceType[]).map((typeKey) => (
                                    <div
                                        key={typeKey}
                                        onClick={() => handleTypeChange(typeKey)}
                                        className={`report-type-card ${formData.types.includes(typeKey) ? 'active' : ''}`}
                                    >
                                        <span className="material-symbols-outlined report-type-icon">{TYPE_ICONS[typeKey]}</span>
                                        <span className="report-type-label">{t(`maintenance.types.${typeKey}`)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end mt-6">
                            <button type="button" onClick={() => handleSectionChange('general')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* GENERAL INFO SECTION */}
                    <div className={activeSection === 'general' ? 'block' : 'hidden'}>
                        <div className="section-panel">
                            <h2 className="section-panel-heading">{t('maintenance.general.title')}</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="form-label form-label-required">{t('maintenance.general.customerName')}</label>
                                    <input
                                        type="text"
                                        name="customerName"
                                        value={formData.customerName}
                                        onChange={handleInputChange}
                                        className={`form-input ${fieldErrors.customerName ? 'input-error' : ''}`}
                                        required
                                    />
                                    {fieldErrors.customerName && (
                                        <span className="error-message">⚠️ {fieldErrors.customerName}</span>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label">{t('maintenance.general.customerAddress')}</label>
                                    <input
                                        type="text"
                                        name="customerAddress"
                                        value={formData.customerAddress}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div>
                                    <label className="form-label">{t('maintenance.general.siteName')}</label>
                                    <input
                                        type="text"
                                        name="siteName"
                                        value={formData.siteName}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div>
                                    <label className="form-label">{t('maintenance.general.siteAddress')}</label>
                                    <input
                                        type="text"
                                        name="siteAddress"
                                        value={formData.siteAddress}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div>
                                    <label className="form-label">{t('maintenance.general.contactPerson')}</label>
                                    <input
                                        type="text"
                                        name="contactPerson"
                                        value={formData.contactPerson}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div>
                                    <label className="form-label">{t('maintenance.general.contactPhone')}</label>
                                    <input
                                        type="tel"
                                        name="contactPhone"
                                        value={formData.contactPhone}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div>
                                    <label className="form-label form-label-required">{t('maintenance.general.date')}</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        className={`form-input ${fieldErrors.date ? 'input-error' : ''}`}
                                        required
                                    />
                                    {fieldErrors.date && (
                                        <span className="error-message">⚠️ {fieldErrors.date}</span>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label form-label-required">{t('maintenance.general.technician')}</label>
                                    <input
                                        type="text"
                                        name="technician"
                                        value={formData.technician}
                                        onChange={handleInputChange}
                                        className={`form-input ${fieldErrors.technician ? 'input-error' : ''}`}
                                        required
                                    />
                                    {fieldErrors.technician && (
                                        <span className="error-message">⚠️ {fieldErrors.technician}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Control Centers */}
                        <div className="section-panel" style={{ marginTop: "var(--spacing-lg)" }}>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="section-panel-heading">{t('maintenance.controlCenters.title')}</h2>
                                <button type="button" onClick={addControlCenter} className="btn btn-secondary text-sm">
                                    <span className="material-symbols-outlined text-sm">add</span>
                                    {t('maintenance.controlCenters.addButton')}
                                </button>
                            </div>

                            <div className="space-y-4">
                                {formData.controlCenters.map((cc, index) => (
                                    <div key={cc.id} className="item-card">
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="font-medium text-gray-700">#{index + 1}</span>
                                            {formData.controlCenters.length > 1 && (
                                                <button type="button" onClick={() => removeControlCenter(cc.id)} className="text-red-500 hover:text-red-700">
                                                    <span className="material-symbols-outlined text-sm">close</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="form-label">{t('maintenance.controlCenters.type')}</label>
                                                <select
                                                    value={cc.type}
                                                    onChange={(e) => updateControlCenter(cc.id, 'type', e.target.value)}
                                                    className="form-select"
                                                >
                                                    {CONTROL_CENTER_TYPES.map(type => (
                                                        <option key={type} value={type}>
                                                            {t(`maintenance.controlCenters.types.${type}`)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {cc.type === 'custom' && (
                                                <>
                                                    <div>
                                                        <label className="form-label">{t('maintenance.controlCenters.customName')}</label>
                                                        <input
                                                            type="text"
                                                            value={cc.customName || ''}
                                                            onChange={(e) => updateControlCenter(cc.id, 'customName', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="form-label">{t('maintenance.controlCenters.customDescription')}</label>
                                                        <input
                                                            type="text"
                                                            value={cc.customDescription || ''}
                                                            onChange={(e) => updateControlCenter(cc.id, 'customDescription', e.target.value)}
                                                            className="form-input"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            {cc.type !== 'none' && cc.type !== 'custom' && (
                                                <div>
                                                    <label className="form-label">{t('maintenance.controlCenters.serialNumber')}</label>
                                                    <input
                                                        type="text"
                                                        value={cc.serialNumber || ''}
                                                        onChange={(e) => updateControlCenter(cc.id, 'serialNumber', e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between mt-6">
                            <button type="button" onClick={() => handleSectionChange('type')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                            <button type="button" onClick={() => handleSectionChange('equipment')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* EQUIPMENT / HOODS SECTION */}
                    <div className={activeSection === 'equipment' ? 'block' : 'hidden'}>
                        <div className="section-panel">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="section-panel-heading">{t('maintenance.hoods.title')}</h2>
                                <button type="button" onClick={addHood} className="btn btn-primary text-sm">
                                    <span className="material-symbols-outlined text-sm">add</span>
                                    {t('maintenance.hoods.addButton')}
                                </button>
                            </div>

                            {formData.hoods.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <span className="material-symbols-outlined text-5xl mb-3">kitchen</span>
                                    <p className="text-lg font-medium">{t('maintenance.hoods.noHoods')}</p>
                                    <p className="text-sm">{t('maintenance.hoods.noHoodsHint')}</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {formData.hoods.map((hood, index) => (
                                        <div key={hood.id} className="item-card">
                                            <div className="flex justify-between items-start mb-4">
                                                <span className="font-semibold text-gray-800 text-lg">#{index + 1}</span>
                                                <button type="button" onClick={() => removeHood(hood.id)} className="text-red-500 hover:text-red-700">
                                                    <span className="material-symbols-outlined">delete</span>
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <label className="form-label">{t('maintenance.hoods.name')}</label>
                                                    <input
                                                        type="text"
                                                        value={hood.name}
                                                        onChange={(e) => updateHood(hood.id, 'name', e.target.value)}
                                                        placeholder={t('maintenance.hoods.namePlaceholder')}
                                                        className="form-input"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('maintenance.hoods.location')}</label>
                                                    <input
                                                        type="text"
                                                        value={hood.location || ''}
                                                        onChange={(e) => updateHood(hood.id, 'location', e.target.value)}
                                                        placeholder={t('maintenance.hoods.locationPlaceholder')}
                                                        className="form-input"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="form-label">{t('maintenance.hoods.technologies')}</label>
                                                <div className="flex flex-wrap gap-2">
                                                    {HOOD_TECHNOLOGIES.map(tech => (
                                                        <button
                                                            key={tech}
                                                            type="button"
                                                            onClick={() => toggleHoodTechnology(hood.id, tech)}
                                                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                                                                hood.technologies.includes(tech)
                                                                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                                                                    : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            {t(`maintenance.technologies.${tech}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {hood.technologies.includes('uv') && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                                    <div>
                                                        <label className="form-label">{t('maintenance.hoods.uvTubeCount')}</label>
                                                        <input
                                                            type="number"
                                                            value={hood.uvTubeCount || ''}
                                                            onChange={(e) => updateHood(hood.id, 'uvTubeCount', e.target.value ? Number(e.target.value) : undefined)}
                                                            className="form-input"
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="form-label">{t('maintenance.hoods.uvHours')}</label>
                                                        <input
                                                            type="number"
                                                            value={hood.uvHours || ''}
                                                            onChange={(e) => updateHood(hood.id, 'uvHours', e.target.value ? Number(e.target.value) : undefined)}
                                                            className="form-input"
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div>
                                                <label className="form-label">{t('maintenance.hoods.notes')}</label>
                                                <textarea
                                                    value={hood.notes || ''}
                                                    onChange={(e) => updateHood(hood.id, 'notes', e.target.value)}
                                                    className="form-input"
                                                    rows={2}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mobichef Pressure Readings */}
                        {formData.types.includes('mobichef') && (
                            <div className="section-panel" style={{ marginTop: "var(--spacing-lg)" }}>
                                <h2 className="section-panel-heading">{t('maintenance.mobichef.pressureReadings')}</h2>
                                <div className="grid grid-cols-3 gap-4">
                                    {(['low', 'medium', 'high'] as const).map(level => (
                                        <div key={level}>
                                            <label className="form-label">{t(`maintenance.mobichef.pressure${level.charAt(0).toUpperCase() + level.slice(1)}`)} ({t('maintenance.mobichef.pressureUnit')})</label>
                                            <input
                                                type="number"
                                                value={formData.mobichefPressure?.[level] || ''}
                                                onChange={(e) => handlePressureChange(level, e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between mt-6">
                            <button type="button" onClick={() => handleSectionChange('general')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                            <button type="button" onClick={() => handleSectionChange('checklist')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* CHECKLIST SECTION */}
                    <div className={activeSection === 'checklist' ? 'block' : 'hidden'}>
                        <div className="section-panel">
                            <h2 className="section-panel-heading">{t('maintenance.checklist.title')}</h2>
                            
                            {checklist.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">
                                    {t('maintenance.hoods.noHoodsHint')}
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {checklist.map((item, index) => (
                                        <div key={item.id} className="checklist-item-card">
                                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                                <div className="flex-1">
                                                    <span className="checklist-question">
                                                        <span className="text-gray-500 mr-2">{index + 1}.</span>
                                                        {item.label}
                                                    </span>
                                                </div>
                                                <div className="status-segmented-control">
                                                    {(['OK', 'NOK', 'NA'] as const).map((status) => (
                                                        <button
                                                            key={status}
                                                            type="button"
                                                            onClick={() => handleChecklistChange(item.id, 'status', status)}
                                                            data-active={item.status === status}
                                                            className={`status-option status-${status.toLowerCase()}`}
                                                        >
                                                            <span className="material-symbols-outlined text-lg">
                                                                {status === 'OK' ? 'check_circle' : status === 'NOK' ? 'cancel' : 'block'}
                                                            </span>
                                                            <span className="hidden sm:inline">{t(`maintenance.checklist.${status.toLowerCase()}`)}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {(item.status === 'NOK' || item.notes) && (
                                                <div className="mt-4 animate-fade-in">
                                                    <input
                                                        type="text"
                                                        placeholder={t('maintenance.checklist.notes')}
                                                        value={item.notes}
                                                        onChange={(e) => handleChecklistChange(item.id, 'notes', e.target.value)}
                                                        className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-gray-50"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* General Notes */}
                        <div className="section-panel" style={{ marginTop: "var(--spacing-lg)" }}>
                            <h2 className="section-panel-heading">{t('maintenance.mobichef.notes')}</h2>
                            <textarea
                                name="generalNotes"
                                value={formData.generalNotes || ''}
                                onChange={handleInputChange}
                                className="form-input w-full"
                                rows={4}
                                placeholder={t('maintenance.parts.partsPlaceholder')}
                            />
                        </div>

                        {/* Parts & Out of Contract Work */}
                        <div className="section-panel" style={{ marginTop: "var(--spacing-lg)" }}>
                            <h2 className="section-panel-heading">{t('maintenance.parts.title')}</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="form-label">{t('maintenance.parts.partsUsed')}</label>
                                    <textarea
                                        name="partsAndConsumables"
                                        value={formData.partsAndConsumables || ''}
                                        onChange={handleInputChange}
                                        className="form-input w-full"
                                        rows={3}
                                        placeholder={t('maintenance.parts.partsPlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label className="form-label">{t('maintenance.parts.outOfContract')}</label>
                                    <textarea
                                        name="outOfContractWork"
                                        value={formData.outOfContractWork || ''}
                                        onChange={handleInputChange}
                                        className="form-input w-full"
                                        rows={3}
                                        placeholder={t('maintenance.parts.outOfContractPlaceholder')}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between mt-6">
                            <button type="button" onClick={() => handleSectionChange('equipment')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                            <button type="button" onClick={() => handleSectionChange('images')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* IMAGES SECTION */}
                    <div className={activeSection === 'images' ? 'block' : 'hidden'}>
                        {/* Before/After Images (for cleaning type) */}
                        {formData.types.includes('kcw_annual_cleaning') && (
                            <div className="section-panel" style={{ marginBottom: "var(--spacing-lg)" }}>
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h2 className="section-panel-heading">{t('maintenance.images.beforeAfter')}</h2>
                                        <p className="text-muted">{t('maintenance.images.beforeAfterHint')}</p>
                                    </div>
                                    <button type="button" onClick={addBeforeAfterPair} className="btn btn-secondary text-sm">
                                        <span className="material-symbols-outlined text-sm">add</span>
                                        {t('maintenance.images.addPair')}
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {beforeAfterImages.map((pair, index) => (
                                        <div key={pair.id} className="item-card">
                                            <div className="flex justify-between items-center mb-3">
                                                <span className="font-medium text-gray-700">#{index + 1}</span>
                                                <button type="button" onClick={() => removeBeforeAfterPair(pair.id)} className="text-red-500 hover:text-red-700">
                                                    <span className="material-symbols-outlined text-sm">close</span>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="form-label">{t('maintenance.images.before')}</label>
                                                    <div className="relative aspect-video bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden">
                                                        {pair.before ? (
                                                            <img src={pair.before} alt="Before" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                                                                <span className="material-symbols-outlined text-3xl">add_photo_alternate</span>
                                                            </div>
                                                        )}
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleBeforeAfterUpload(pair.id, 'before', e)}
                                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('maintenance.images.after')}</label>
                                                    <div className="relative aspect-video bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden">
                                                        {pair.after ? (
                                                            <img src={pair.after} alt="After" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                                                                <span className="material-symbols-outlined text-3xl">add_photo_alternate</span>
                                                            </div>
                                                        )}
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleBeforeAfterUpload(pair.id, 'after', e)}
                                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* General Images */}
                        <div className="section-panel">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h2 className="section-panel-heading">{t('maintenance.images.generalImages')}</h2>
                                    <p className="text-muted">{t('maintenance.images.generalImagesHint')}</p>
                                </div>
                                <span className="text-muted">{generalImages.length} {t('maintenance.images.count')}</span>
                            </div>

                            <div className="upload-dropzone relative">
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleGeneralImageUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                />
                                <div className="text-gray-500 flex flex-col items-center">
                                    <span className="material-symbols-outlined text-4xl text-blue-500 mb-2">cloud_upload</span>
                                    <p className="text-lg font-medium mb-1">{t('maintenance.images.dropzone')}</p>
                                    <p className="text-sm text-gray-400">JPG, PNG (Max 5MB)</p>
                                </div>
                            </div>

                            {generalImages.length > 0 && (
                                <div className="image-upload-grid mt-4">
                                    {generalImages.map((img, idx) => (
                                        <div key={idx} className="image-preview-card">
                                            <img src={img} alt={`Preview ${idx}`} />
                                            <div className="delete-overlay" onClick={() => removeGeneralImage(idx)}>
                                                <span className="material-symbols-outlined delete-icon">delete</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between mt-6">
                            <button type="button" onClick={() => handleSectionChange('checklist')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>

                            {/* Mobile Submit */}
                            <div className="lg:hidden flex gap-2">
                                <button type="button" onClick={handleSaveToArchive} className="btn btn-success">
                                    <span className="material-symbols-outlined text-sm">save</span>
                                    {t('maintenance.actions.save')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <span className="material-symbols-outlined text-sm">print</span>
                                    {t('maintenance.actions.print')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default MaintenanceReportForm;
